import React, { useEffect } from 'react';
import useTranslate from '../useTranslate';
import { RequestStatus } from '../../store/network/managedRequest';
import {
    CreateNewAccountingAreaInviteForEmail,
    requiresPaymentConfirmation,
    SUB_ROLES_FOR_EXTERNAL,
    SUB_ROLES_FOR_INTERAL,
    UserSetupProcessRole,
    UserSetupProcessSubRole,
} from '../../store/accountingAreaRolesSlice';
import ValidatorFormGroup from '../main/validator-form-group';
import EditLargeCardFormik, {
    EditLargeCardFormikProps,
} from '../main/edit-large-card-formik';
import { FormControl } from 'react-bootstrap';
import { goBack } from '../../nav/navigationService';
import { PartnerManagerControl } from '../../store/partnerManagerSlice';
import { PbRequest } from '../../store/pbRequest';
import { pbRequestReset } from '../../store/pbRequestSlice';
import { usePbDispatch, usePbSelector } from '../../store/pbStoreHooks';

const E_MAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface RoleAddUserCardValues {
    email: string;
    role: UserSetupProcessRole;
    subRole: UserSetupProcessSubRole;
    paymentConfirmation: boolean;
}

const initialValues: RoleAddUserCardValues = {
    email: '',
    role: UserSetupProcessRole.B2B_LAUNDRY_USAGE,
    subRole: UserSetupProcessSubRole.RECEPTIONIST,
    paymentConfirmation: false,
};

const textHint: React.CSSProperties = {
    color: '#006db6',
};

function RolesAddUserCard() {
    const strings = useTranslate('RolesAddUserCard');
    const roleStrings = useTranslate('UserActivationOverview');
    const dispatch = usePbDispatch();
    const createInviteRequest = usePbSelector(
        (state) => state.pbRequest[PbRequest.POST_INVITE_FOR_ACCOUNTING_AREA]
    );
    const partnerManagerDetails = usePbSelector(
        (state) => state.partnerManager.partnerManagerDetails
    );

    const isAdmin =
        partnerManagerDetails?.control === PartnerManagerControl.ADMIN;

    useEffect(() => {
        dispatch(pbRequestReset(PbRequest.POST_INVITE_FOR_ACCOUNTING_AREA));
    }, []);

    useEffect(() => {
        if (createInviteRequest.status === RequestStatus.SUCCESS) {
            goBack();
        }
    }, [createInviteRequest]);

    function renderRequestStatus() {
        function renderErrorTagToMessageOrUnknownError(errorTag: string) {
            const errorText = strings.errors[errorTag]
                ? strings.errors[errorTag]
                : strings.errors['UNKNOWN_ERROR'];
            return (
                <div className="alert alert-danger">
                    <i className="fa fa-exclamation-circle fa-fw" /> {errorText}
                </div>
            );
        }

        if (createInviteRequest.status === RequestStatus.FAILURE) {
            const errors = createInviteRequest.errors
                ? createInviteRequest.errors
                : [];
            return (
                <>
                    {errors.map((error) => {
                        return renderErrorTagToMessageOrUnknownError(error);
                    })}
                </>
            );
        }
        return <></>;
    }

    function renderForm(
        formProps: EditLargeCardFormikProps<RoleAddUserCardValues>
    ) {
        const subRoles = isAdmin
            ? SUB_ROLES_FOR_INTERAL
            : SUB_ROLES_FOR_EXTERNAL;

        return (
            <div>
                <ValidatorFormGroup
                    label={strings.emailAddress}
                    hideErrorLabel={!formProps.errors.email}
                    errorLabel={
                        formProps.errors.email
                            ? strings.errors[formProps.errors.email]
                            : ''
                    }
                >
                    <FormControl
                        disabled={!formProps.active}
                        type={'text'}
                        value={formProps.values.email}
                        onChange={formProps.handleChange}
                        name={'email'}
                        required
                    />
                </ValidatorFormGroup>
                <ValidatorFormGroup label={strings.role}>
                    <FormControl
                        disabled={true}
                        type={'text'}
                        value={
                            roleStrings.roleTranslations[formProps.values.role]
                        }
                        name={'role'}
                        required
                    />
                </ValidatorFormGroup>
                <ValidatorFormGroup label={strings.subRole}>
                    <select
                        className="form-control"
                        disabled={!formProps.active}
                        value={formProps.values.subRole}
                        onChange={formProps.handleChange}
                        name={'subRole'}
                    >
                        {subRoles.map((value) => (
                            <option key={value} value={value}>
                                {roleStrings.subRoleTranslations[value]}
                            </option>
                        ))}
                    </select>
                </ValidatorFormGroup>
                {requiresPaymentConfirmation(formProps.values.role) && (
                    <div className="checkbox checkbox-primary checkbox-custom">
                        <input
                            type="checkbox"
                            id={'paymentConfirmation'}
                            className="styled"
                            disabled={!formProps.active}
                            checked={formProps.values.paymentConfirmation}
                            onChange={formProps.handleChange}
                            required
                            name={'paymentConfirmation'}
                        />
                        <label htmlFor="paymentConfirmation">
                            {strings.paymentConfirmation}
                        </label>
                    </div>
                )}
                {renderRequestStatus()}
            </div>
        );
    }

    function handleSubmit(values: RoleAddUserCardValues) {
        dispatch(CreateNewAccountingAreaInviteForEmail(values));
    }

    function handleCancel() {
        goBack();
    }

    function validateForm(values: RoleAddUserCardValues) {
        const errors: Partial<{
            email: string;
            role: string;
            subRole: string;
        }> = {};
        if (!E_MAIL_PATTERN.test(values.email)) {
            errors.email = 'invalidEmail';
        }
        return errors;
    }

    return (
        <EditLargeCardFormik
            active_on_init
            title={strings.title}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            submitLabel={strings.title}
            error={createInviteRequest.status === RequestStatus.FAILURE}
            initialValues={initialValues}
            validate={validateForm}
        >
            {renderForm}
        </EditLargeCardFormik>
    );
}

export default RolesAddUserCard;
